import { create } from 'zustand';

import { convertUrlInBucket } from '../../api/cdn';
import { getStudentEmailDomain } from '../../api/environment';
import { HomeworkSubmissionRepository } from '../../api/repository/homework-submission';
import { userErrorMessage } from '../../stores/userError';
import { courseTitles } from './courseTitles';
import { emailPattern, toSubmission, STUDENT_EMAIL_DOMAIN_UNSET, type StudentSubmission } from './submissions';

// a class over several school years stays well under this; older rows drop first
const SUBMISSION_LIMIT = 500;

// only the newest load may write, so a slow earlier roster never replaces the current class
let loadSeq = 0;

export interface SubmissionState {
  key: string | null;
  loading: boolean;
  error: string | null;
  submissions: StudentSubmission[];
  courseTitles: Record<string, string>;
  load: (key: string, codes: string[], force?: boolean) => Promise<void>;
}

export const useSubmissionStore = create<SubmissionState>((set, get) => ({
  key: null,
  loading: false,
  error: null,
  submissions: [],
  courseTitles: {},

  async load(key, codes, force = false) {
    const state = get();
    if (!force && state.key === key && !state.error && (state.loading || state.submissions.length > 0)) return;
    loadSeq += 1;
    const seq = loadSeq;
    const domain = getStudentEmailDomain();
    if (!domain) { set({ key, loading: false, error: STUDENT_EMAIL_DOMAIN_UNSET, submissions: [] }); return; }
    const pattern = emailPattern(codes, domain);
    if (!pattern) { set({ key, loading: false, error: null, submissions: [] }); return; }
    set({ key, loading: true, error: null, submissions: state.key === key ? state.submissions : [] });
    try {
      const rows = await HomeworkSubmissionRepository.listByEmail(pattern, SUBMISSION_LIMIT);
      if (seq !== loadSeq) return;
      const submissions: StudentSubmission[] = [];
      rows.forEach((row) => {
        const submission = toSubmission(row, domain, convertUrlInBucket);
        if (submission) submissions.push(submission);
      });
      set({ loading: false, submissions });
      // titles only label the rows, so the list shows before they land
      const titles = await courseTitles(submissions.map((s) => s.courseId).filter((id, i, all) => all.indexOf(id) === i), get().courseTitles);
      if (seq !== loadSeq) return;
      set({ courseTitles: titles });
    } catch (error) {
      if (seq !== loadSeq) return;
      set({ loading: false, error: userErrorMessage(error) });
    }
  },
}));
